import {Component, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {OrganizationsService} from '../../services/organizations.service';
import {BasicFilters} from '../../model/filters/basic-filters';
import {SortOrder} from '../../model/sort-order';
import {CommonMessagesService} from '../../services/common-messages.service';
import {TenantsService} from '../../services/tenants.service';
import {AuthGuard} from '../../common/auth-guard.service';
import {FiltersService} from '../../services/filters.service';
import {SequencesService} from '../../services/sequences.service';
import {SequenceSubscriptionInputComponent} from './sequences-subscription/sequence-subscription-input.component';
import {Organization} from '../../model/organization';
import {LoggedUser, User} from '../../common/logged-user.service';
import {Tenant} from '../../model/tenant';
import {OrganizationsResponse} from '../../model/responses/organizations-response';
import {Sequence, SequenceState} from '../../model/sequence';
import {cloneDeep} from 'lodash';
import {DeleteResponse} from '../../model/responses/delete-response';
import {DeletesResponse} from '../../model/responses/deletes-response';
import {SequencesResponse} from '../../model/responses/sequences-response';
import {translate} from '@ngneat/transloco';
import {SequenceFilters} from '../../model/filters/sequence-filters';
import * as FileSaver from 'file-saver';
import {interval, Observable, Subscription} from 'rxjs';

@Component({
  selector: 'app-organizations',
  templateUrl: './sequences.component.html',
  styleUrls: ['./sequences.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SequencesComponent implements OnInit, OnDestroy {
  @ViewChild(SequenceSubscriptionInputComponent) sequenceSubscriptionInputComponent: SequenceSubscriptionInputComponent;
  public rows = 10;
  public first = 0;
  public selected: any[] = [];

  public sequences: Sequence[];
  public totalSequences: number;
  public loadingSequences: boolean;

  public filtersVisible: boolean;
  public currentFilters: SequenceFilters;
  public prevFilters: SequenceFilters;
  public filtersFilled: boolean;

  public tenants: Tenant[];

  public organizations: Organization[];
  public userOrganizations: Organization[];

  public tenantFilterChanged: boolean;

  public tenantUuidOrganizationsCacheMap: Map<string, Pick<Organization, 'name' | 'uuid'>[]>;

  public stateOptions: any[];

  private subscription: Subscription;
  constructor(private organizationsService: OrganizationsService,
              private sequencesService: SequencesService,
              public tenantsService: TenantsService,
              public authGuardService: AuthGuard,
              private loggedUser: LoggedUser,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<SequenceFilters>
  ) {
    this.currentFilters = new SequenceFilters();
    this.prevFilters = new SequenceFilters();
    this.sequences = [];
  }

  ngOnInit(): void {
    this.loadStatesOptions();
    this.loggedUser.userObservable.subscribe((user: User): void => {
      this.tenants = user.tenants;
      this.organizations = user.organizations;
      this.userOrganizations = user.organizations;
      this.tenantUuidOrganizationsCacheMap = new Map();
    });

    const source: Observable<number> = interval(60000);
    this.subscription = source.subscribe(() => this.loadSequences(this.first / this.rows, this.rows));
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

  private loadStatesOptions(): void {
    const keys: string[] = Object.keys(SequenceState);
    this.stateOptions = [];
    keys.forEach((key: string): void => {
      this.stateOptions.push({label: 'misc.sequenceState.' + SequenceState[key].toLowerCase(), value: key});
    });
  }

  private loadSequences(offset?: number, limit?: number): void {
    this.loadingSequences = true;
    this.sequences = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);

    const sequenceFilters: SequenceFilters = this.filtersService.getCurrentFilters(this.constructor.name) ?? this.currentFilters;

    this.sequencesService.getSequences(offset, limit, sequenceFilters)
      .subscribe((response: SequencesResponse): void => {
        this.sequences = response.sequences;
        this.totalSequences = response.totalElements;
      }, (): void => {
      }, (): void => {
        this.loadingSequences = false;
      });
  }

  onPageChange(event: any): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadSequences(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.organizations = this.userOrganizations;
    this.currentFilters = new SequenceFilters();
    this.prevFilters = new SequenceFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadSequences(this.first / this.rows, this.rows);
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  confirmAndDelete(sequenceUuid: string): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSequence(sequenceUuid), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) { return; }

    const uuids: { uuid: string }[] = this.selected.map(item => item.uuid);

    this.sequencesService.deleteSequences(uuids).toPromise()
      .then((response: DeletesResponse): void => {
        if (response.deleted) {
          this.commonMessagesService.showSuccessfullyDeletedMessage();
          this.selected = [];
          this.loadSequences(this.first / this.rows, this.rows);
        } else {
          this.commonMessagesService.showErrorWhileDeletingMessage(404, true);
        }
      }, error => {
        this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
      });
  }

  deleteSequence(sequenceUuid: string): void {
    this.sequencesService.deleteSequence(sequenceUuid).toPromise()
      .then((response: DeleteResponse): void => {
        if (response.deleted) {
          this.commonMessagesService.showSuccessfullyDeletedMessage();
          this.selected = this.selected.filter((element): boolean => element.uuid !== sequenceUuid);
          this.loadSequences(this.first / this.rows, this.rows);
        } else if (response.found) {
          this.commonMessagesService.showErrorWhileDeletingMessage(409);
        } else {
          this.commonMessagesService.showErrorWhileDeletingMessage(404);
        }
      }, error => {
        this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
      });
  }

  downloadSequence(sequenceUuid: string, fileName: string): void {
    this.sequencesService.downloadSequenceConfig(sequenceUuid).subscribe(response => {
      const JSON_EXTENSION = '.json';
      const data: Blob = new Blob([JSON.stringify(response)], {
        type: 'text/json'
      });
      FileSaver.saveAs(data, fileName + JSON_EXTENSION);

      return response;
    });
  }

  restartSequence(sequence: Sequence): void {
    this.commonMessagesService.showRestartSequenceConfirmation(() => this.sequencesService.restartSequence(sequence.uuid)
      .subscribe(() => {
        this.loadSequences(this.first / this.rows, this.rows);
      }), sequence.name);
  }

  executeSequence(sequence: Sequence): void {
    const editedSequence: Sequence = cloneDeep(sequence);

    this.commonMessagesService.showSequenceExecuteConfirmation(() => {
      this.sequencesService.executeSequence(editedSequence.uuid).subscribe(response => {
        this.loadSequences(this.first / this.rows, this.rows);
      });
    }, sequence.name);
  }

  openSubscription(sequenceUuid: string): void {
    this.sequenceSubscriptionInputComponent.openSubscription(sequenceUuid);
  }

  trimFieldsForm(filters: BasicFilters): void {
    Object.keys(filters).forEach((key: string): void => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  async filteredOrganizations(): Promise<void> {
    if (this.authGuardService.superAdminRequired()) {
      this.tenantFilterChanged = this.filterChanged();

      if (this.currentFilters.tenantUuids?.length > 0 && this.tenantFilterChanged) {
        const filter: BasicFilters = new BasicFilters();
        filter.tenantUuids = [];

        this.currentFilters.tenantUuids.forEach((tenantUuid: string): void => {
          if (!this.tenantUuidOrganizationsCacheMap.has(tenantUuid)) {
            filter.tenantUuids.push(tenantUuid);
          }
        });

        if (filter.tenantUuids.length > 0) {
          const response: OrganizationsResponse = await this.organizationsService.getOrganizations(undefined, undefined, filter)
            .toPromise();
          response.organizations.forEach((organization: Organization) => {
            if (!this.tenantUuidOrganizationsCacheMap.has(organization.tenant.uuid)) {
              this.tenantUuidOrganizationsCacheMap.set(organization.tenant.uuid, [{
                name: organization.name,
                uuid: organization.uuid
              }]);
            } else {
              this.tenantUuidOrganizationsCacheMap.get(organization.tenant.uuid).push({
                name: organization.name,
                uuid: organization.uuid
              });
            }
          });
        }

        let filterOrganization: Pick<Organization, 'name' | 'uuid'> [] = [];
        this.currentFilters.tenantUuids.forEach((tenantUuid: string): void => {
          const organizations: Pick<Organization, 'name' | 'uuid'> [] = this.tenantUuidOrganizationsCacheMap.get(tenantUuid);
          if (organizations) {
            filterOrganization = [...filterOrganization, ...organizations];
          }
        });

        this.organizations = filterOrganization as Organization[];
        this.prevFilters.tenantUuids = [...this.currentFilters.tenantUuids];
        this.tenantFilterChanged = false;


      } else if (this.currentFilters.tenantUuids?.length === 0) {
        this.prevFilters.tenantUuids = [];
        this.organizations = this.userOrganizations;
        this.tenantFilterChanged = false;
      }
    }
  }

  filterChanged(): boolean {
    if (this.prevFilters.tenantUuids?.length === this.currentFilters.tenantUuids?.length) {
      return !this.prevFilters.tenantUuids.every((tenantUuid: string) => this.currentFilters.tenantUuids.includes(tenantUuid));
    }
    return true;
  }

  getStateSeverity(status: SequenceState): string {
    switch (status) {
      case SequenceState.NOT_COMPLETED:
        return 'sequence-not-completed';
      case SequenceState.EXECUTING:
        return 'sequence-executing';
      case SequenceState.COMPLETED:
        return 'sequence-completed';
      case SequenceState.WAITING:
        return 'sequence-waiting';
      case SequenceState.FAILED:
        return 'sequence-failed';
      case SequenceState.RESTORED:
        return 'sequence-restored';
      default:
        return '';
    }
  }

  getStateTranslation(status: string): string {
    if (status) {
      const translatedStatus: string = translate(`misc.sequenceState.${status.toLowerCase()}`);
      return translatedStatus.toUpperCase();
    }
    return '';
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

  public canUpdate(sequence: Sequence): boolean {
    return SequenceState.EXECUTING !== sequence.status;
  }
  public canExecute(sequence: Sequence): boolean {
    const validExec = [SequenceState.COMPLETED, SequenceState.WAITING, SequenceState.RESTORED];

    return validExec.indexOf(sequence.status) >= 0;
  }
  public canRestore(sequence: Sequence): boolean {
    const invalidRestore = [SequenceState.EXECUTING, SequenceState.WAITING];

    return !(invalidRestore.indexOf(sequence.status) >= 0);
  }
  public canDelete(sequence: Sequence): boolean {
    return SequenceState.EXECUTING !== sequence.status;
  }
}
